import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';

const AgentAccessControl = ({ agentId, children, fallbackContent }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  useEffect(() => {
    checkAgentAccess();
  }, [user, agentId]);

  const checkAgentAccess = async () => {
    if (!user) {
      setHasAccess(false);
      setLoading(false);
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${backendUrl}/api/subscription/check-agent-access/${agentId}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setHasAccess(data.has_access);
        setSubscription({
          subscription_name: data.subscription_name,
          selected_agents: data.selected_agents
        });
        setSelectedAgents(data.selected_agents || []);
      } else {
        console.error('Failed to fetch agent access data:', response.status);
        setHasAccess(false);
      }
    } catch (error) {
      console.error('Error checking agent access:', error);
      setHasAccess(false);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradeClick = () => {
    navigate('/pricing');
  };

  const handleAgentSelection = () => {
    // Open agent selection modal or navigate to agent selection
    window.location.reload(); // This will trigger agent selection check
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
        <span className="ml-2 text-gray-400">Checking access...</span>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center p-8">
        <div className="text-6xl mb-4">🔒</div>
        <h3 className="text-xl font-bold text-white mb-2">Sign In Required</h3>
        <p className="text-gray-400 mb-4">Please sign in to access AI agents</p>
        <button 
          onClick={() => navigate('/')}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium"
        >
          Go to Sign In
        </button>
      </div>
    );
  }

  if (!hasAccess) {
    const subscriptionName = subscription?.subscription_name || 'Explorer';
    
    if (fallbackContent) {
      return fallbackContent;
    }

    return (
      <div className="text-center p-8">
        <div className="text-6xl mb-4">🔒</div>
        <h3 className="text-xl font-bold text-white mb-2">Agent Locked</h3>
        
        {subscriptionName === 'Explorer' && (
          <div>
            <p className="text-gray-400 mb-4">
              Your {subscriptionName} plan allows access to 1 agent. You may have reached your limit or need to upgrade.
            </p>
            <div className="space-y-2">
              <button 
                onClick={handleUpgradeClick}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium block w-full"
              >
                Upgrade to Access More Agents
              </button>
            </div>
          </div>
        )}
        
        {(subscriptionName === 'Navigator' || subscriptionName === 'Advocate') && (
          <div>
            <p className="text-gray-400 mb-4">
              Your {subscriptionName} plan allows {subscriptionName === 'Navigator' ? '1' : '4'} selected agent(s). 
              This agent is not in your selection.
            </p>
            <div className="space-y-2">
              <button 
                onClick={handleAgentSelection}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium block w-full"
              >
                Manage Agent Selection
              </button>
              <button 
                onClick={handleUpgradeClick}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium block w-full"
              >
                Upgrade Plan
              </button>
            </div>
          </div>
        )}

        <div className="mt-6 p-4 bg-gray-800 rounded-lg">
          <h4 className="text-white font-medium mb-2">Current Plan: {subscriptionName}</h4>
          {selectedAgents.length > 0 && (
            <div>
              <p className="text-gray-300 text-sm mb-1">Selected Agents:</p>
              <p className="text-blue-400 text-sm">{selectedAgents.join(', ')}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return children;
};

export default AgentAccessControl;